<template>
<v-form>
  <v-card flat class='grey lighten-4' >
    <request-status :request='paymentRequest' />
    <v-card-text v-if='isSuccess' class='text-center' style='text-align:center;' >

        <v-badge overlap >
          <template v-slot:badge>
            <v-icon dark >check_circle</v-icon>
          </template>
          <v-icon :size='72' color='green' class='pt-2' >paid</v-icon>
        </v-badge>
        <h3 class='subheading' >
          Payment tracked</h3>
        <v-btn class='mb-0'
          v-if='totalDue > 0'
          :disabled='statementLoading'
          @click='reset()' flat >Add another payment</v-btn><br/>
        <v-btn class='mt-0' @click='reset();$emit("close")' flat >Done</v-btn>

    </v-card-text>
    <template >
      <v-card-text >
        <read-only-statement-list initially-collapsed
          v-show='withAppointmentInfo'
          :appointment-ids='computedAppointmentIds'
          :relationships.sync='relationships'
          :due.sync='totalDue'
          :touch='statementTouch'
          :loading.sync='statementLoading'
          @update:currency='setCurrency' >

          <template slot='list-tile' >
            <v-list-tile-action >
              <v-checkbox v-model='paidInFull' ></v-checkbox>
            </v-list-tile-action>
            <v-list-tile-content @click='paidInFull=!paidInFull' >
              <v-list-tile-title>Paid in full</v-list-tile-title>
            </v-list-tile-content>
          </template>

          <v-progress-linear slot='list-end' :value='percentPaid' class='pa-0' height='2' />

        </read-only-statement-list>

        <template v-if='!isSuccess' >

          <v-flex xs12 class='mt-3' >
            <v-dialog ref="dialog" v-model="dialog"
              persistent lazy full-width width="290px" >
              <v-text-field slot="activator" persistent-hint
                v-model="payment.date"
                hint='The date on which you received this payment'
                label="Payment date" prepend-icon="event" readonly
              ></v-text-field>
              <v-date-picker v-model="payment.date" scrollable reactive >
                <v-btn @click='dialog=false' flat >Cancel</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>

          <v-flex xs12 >
            <v-text-field
              :disabled='paidInFull'
              :prefix="currency"
              v-model="payment.amount"
              label="Payment amount"
              type='number'
              required >
            </v-text-field>
          </v-flex>

          <v-flex xs12 >
            <v-text-field
              v-model="payment.reference"
              label="Reference"
              hint="(optional) - record a reference for this payment" >
            </v-text-field>
          </v-flex>

          <template >
            <v-subheader class='overline my-0 px-0' >Payment method</v-subheader>
            <v-chip small
              v-for='method in paymentMethods' :key='method.value'
              @click='payment.payment_method = method.value'
              class='mt-0' >
              <v-icon left small
                v-if='payment.payment_method === method.value' >check</v-icon>
              {{method.text}}
            </v-chip>
          </template>

          <v-alert type='error' :value='paymentExceedsAppointments' >
            <strong>Warning!</strong>
            You are about to add a payment larger than the value
            of the appointments. You can do this .. but it might not be a good idea.
          </v-alert>
        </template>
        <template v-if='withActions && !isSuccess' >
          <v-divider class='mt-3' />
          <v-card-actions class='mb-5' >
            <span v-if='totalDue && dueAfterPayment !== false' >
              <v-chip v-if='dueAfterPayment > 0' >
                <strong>{{ formatCurrency(currency, dueAfterPayment) }}</strong> &nbsp; still owed to you
              </v-chip>
              <v-chip outline color='warning' v-if='dueAfterPayment < 0' >
                <v-icon >warning</v-icon> Payment amount higher than the value of these appointments
              </v-chip>
              <v-chip color='success' dark v-if='dueAfterPayment === 0' >
                <v-icon left >check</v-icon> Paid in full
              </v-chip>
            </span>
            <v-spacer/>
            <span v-if='isValid !== true' class='red--text caption' >
              <v-icon color='error' class='mr-1' small>warning</v-icon>
              {{isValid}}
            </span>
          </v-card-actions>

          <v-card-actions :class='mobileCardActionsClasses' :style='mobileCardActionsStyles' >
            <v-btn flat
              v-if='withDone'
              @click='$emit("close")' >Cancel</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              @click='trackPayment'
              :disabled='isValid !== true'
              :loading='paymentRequest.loading'
              color='primary' dark >Add payment</v-btn>
          </v-card-actions>

        </template>
      </v-card-text>
    </template>
  </v-card>
</v-form>
</template>

<script>
import {
  PAYMENT_METHODS
} from '@/store/constants'

import ClientListItem from '@/components/client/ClientListItem.vue'
import ReadOnlyStatementList from '@/components/lists/ReadOnlyStatementList.vue'
import {DateTime} from 'luxon'
import Vue from 'vue'

export default {
  name: 'CreateTransaction',
  components: {
    ReadOnlyStatementList,
    ClientListItem
  },
  props: {
    appointment: { type: Object },
    appointments: { type: Array, default: () => { return [] } },
    appointmentIds: { type: Array, default: () => { return [] } },
    value: { type: Object },
    loading: { type: Boolean, default: false },
    withActions: { type: Boolean, default: true },
    floatingActions: { type: Boolean, default: false },
    withDone: { type: Boolean, default: true },
    withAppointmentInfo: { type: Boolean, default: true },
    touch: { type: Number, default: 0 }
  },
  data () {
    return {
      active: 0,
      dialog: false,
      paidInFull: false,
      success: false,
      statementTouch: 0,
      statementLoading: false,
      paymentMethods: PAYMENT_METHODS,
      totalDue: null,
      relationships: [],
      payment: {
        date: null,
        amount: 0,
        payment_method: 'eft',
        currency: 'ZAR'
      },
      paymentRequest: {
        resource: 'invoiceguru.transactions',
        status: 0
      },
      sendReceipt: true,
      done: false
    }
  },
  mounted () {
    if (!this.payment.date) {
      this.payment.date = DateTime.local().toISODate()
    }
    if (this.totalDue) {
      this.payment.amount = this.totalDue
    }
  },
  watch: {
    value () {
      if (this.value) { this.payment = this.value }
    },
    payment () {
      // parseFloat("123.456").toFixed(2);
      this.$emit('input', this.payment)
    },
    paidInFull () {
      this.payment.amount = this.totalDue
    },
    paymentDate () {
      this.dialog = false
    },
    paymentRequestStatus () {
      if (this.paymentRequestStatus === 201) {
        this.statementTouch++
        this.payment.amount = 0
        this.$emit('success')
      }
    },
    touch () {
      this.reset()
    }
  },
  methods: {
    trackPayment () {
      this.paymentRequest.data = this.payload
      this.$request('save', this.paymentRequest)
    },
    setCurrency (currency) {
      Vue.set(this.payment, 'currency', currency)
    },
    reset () {
      this.success = false
      this.paidInFull = false
      Vue.set(this.payment, 'reference', null)
      Vue.set(this.payment, 'amount', 0)
      Vue.set(this.paymentRequest, 'status', 0)
    }
  },
  computed: {
    paymentRequestStatus () {
      return this.paymentRequest.status
    },
    currency () {
      return this.payment.currency
    },
    percentPaid () {
      if (this.totalDue > 0 && this.payment.amount > 0) {
        return parseFloat(this.payment.amount) / parseFloat(this.totalDue) * 100
      }
      return 0
    },
    isSuccess () {
      return (this.paymentRequestStatus === 201)
    },
    paymentDate () {
      return this.payment.date
    },
    computedAppointmentIds () {
      if (this.appointmentIds && this.appointmentIds.length > 0) {
        return this.appointmentIds
      }
      if (this.appointments && this.appointments.length > 0) {
        return this.appointments.map((a) => { return a.id })
      }
      if (this.appointment) { return [this.appointment.id] }
      return []
    },
    relationship () {
      if (this.relationships) {
        if (Array.isArray(this.relationships) && this.relationships.length > 0) {
          return this.relationships[0]
        }
        if (!Array.isArray(this.relationships)) { return this.relationships }
      }
      return null
    },
    relationshipId () {
      if (this.relationship && this.relationship.id) {
        return this.relationship.id
      }
      return null
    },
    userId () {
      if (this.relationship && this.relationship.client) {
        return this.relationship.client
      }
    },
    payload () {
      let dt = DateTime.fromISO(this.payment.date).toISODate()
      let tm = DateTime.local().toISOTime()
      let dtme = dt + 'T' + tm

      return {
        actor_id: this.me.id,
        practitioner_id: this.me.id,
        customer_id: this.userId,
        relationship_id: this.relationshipId,
        method: this.payment.payment_method,
        reference: this.payment.reference,
        date: dtme,
        appointments: this.computedAppointmentIds,
        amount: this.payment.amount,
        type: 'Payment'
      }
    },
    dueAfterPayment () {
      if (this.payment) {
        return parseFloat(this.totalDue) - parseFloat(this.payment.amount)
      }
      return false
    },
    paymentExceedsAppointments () {
      let remaining = parseFloat(this.totalDue) - parseFloat(this.payment.amount)
      return remaining < 0
    },
    isValid () {
      if (!this.payment.amount) { return 'Please choose an amount' }
      if (!this.payment.payment_method) { return 'Please choose a payment method' }
      if (!this.payment.date) { return 'Please choose an amount' }
      if (!this.payload.relationship_id) { return 'No relationship found' }
      if (!this.payload.customer_id) { return 'No customer id specified' }
      return true
    }
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// CreateTransaction.vue?3d63ca7d